'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import {ArticleDocumentCard} from '@/components/articles/ArticlesGrid'
import type {ArticleDocument} from '@/types/sanity'

type BlogPostsCarouselClientProps = {
  articles: ArticleDocument[]
  fallbackImageUrl?: string
  cardsPerView?: number
  locale?: string
}

export function BlogPostsCarouselClient({
  articles,
  fallbackImageUrl,
  cardsPerView = 3,
  locale,
}: BlogPostsCarouselClientProps) {
  if (!articles.length) return null

  const normalizedCardsPerView = Math.min(4, Math.max(1, cardsPerView))
  const itemBasisClass = carouselItemBasisClass(normalizedCardsPerView)
  const showNavigation = articles.length > normalizedCardsPerView

  return (
    <Carousel
      opts={{
        align: 'start',
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
      }}
      className="relative mt-site-lg"
    >
      <CarouselContent>
        {articles.map((article, index) => (
          <CarouselItem key={`${article._id}-${index}`} className={itemBasisClass}>
            <ArticleDocumentCard
              article={article}
              fallbackImageUrl={fallbackImageUrl}
              cardClassName="h-full shadow-[0px_1px_6px_rgba(0,0,0,0.12)] hover:shadow-[0px_2px_10px_rgba(0,0,0,0.16)]"
              equalizeContentHeight
              locale={locale}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      {showNavigation ? (
        <>
          <CarouselPrevious className="top-1/2 left-2 -translate-y-1/2 md:-left-12 md:left-auto" />
          <CarouselNext className="top-1/2 right-2 -translate-y-1/2 md:-right-12" />
        </>
      ) : null}
    </Carousel>
  )
}

function carouselItemBasisClass(cardsPerView: number): string {
  if (cardsPerView <= 1) return 'basis-full'
  if (cardsPerView === 2) return 'basis-full md:basis-1/2'
  if (cardsPerView === 4) return 'basis-full md:basis-1/2 lg:basis-1/4'
  return 'basis-full md:basis-1/2 lg:basis-1/3'
}
